// The quick views at the top of Ops › Tenants (owner, 2026-11-03): All, Active,
// Signups, Ops trial, Past due, Suspended, Canceled, Deleted, in one row. A
// signup is a tenant (owner, November 3): Signups is the same tenant list with
// signup=true (/tenants?view=signups), the self-serve leagues that never paid.
// Deleted (/tenants?view=deleted) is the signups whose league the cleanup or Ops
// deleted: they're no longer leagues, so their rows come from the signup record.
// Every other view is a status (/tenants?status=), a paying customer's.

import { useNavigate } from 'react-router-dom'
import { useQuery } from '@/lib/query'
import { api } from '@/api'
import { ACCOUNT_STATUSES, type AccountStatus } from '@/api/types'
import { FilterChips } from '@/components/ui'
import { accountStatusLabel } from '@/lib/status'

export type TenantView = AccountStatus | 'all' | 'signups' | 'deleted'

const LABEL: Record<TenantView, string> = {
  all: 'All',
  active: accountStatusLabel('active'),
  signups: 'Signups',
  // A league made in Ops on its trial; a self-serve trial is under Signups (owner, 2026-11-03).
  trialing: 'Ops trial',
  past_due: accountStatusLabel('past_due'),
  suspended: accountStatusLabel('suspended'),
  canceled: accountStatusLabel('canceled'),
  deleted: 'Deleted',
}

/** All, Active, Signups, the other statuses, then Deleted. */
const ORDER: TenantView[] = ['all', 'active', 'signups', ...ACCOUNT_STATUSES.filter((s) => s !== 'active'), 'deleted']

/** The URL of a view, starting it fresh. */
function viewPath(v: TenantView): string {
  if (v === 'all') return '/tenants'
  if (v === 'signups' || v === 'deleted') return `/tenants?view=${v}`
  return `/tenants?status=${v}`
}

export function TenantViewChips({
  value,
  counts,
  onView,
}: {
  value: TenantView
  /** The tenant list's counts, when the page has them (they follow its search and filters). */
  counts?: Partial<Record<AccountStatus | 'all' | 'signups', number>>
  /** On the tenant list: change the view in place (keeping search and filters). Elsewhere a chip opens the view. */
  onView?: (view: TenantView) => void
}) {
  const navigate = useNavigate()
  const tenants = useQuery(counts ? null : ['tenants', 'count'], (signal) => api.tenants.list({ page_size: 1 }, signal))
  const deleted = useQuery(['signups', 'deleted-count'], (signal) => api.growth.signups.list({ state: 'deleted', page_size: 1 }, signal))
  const listCounts = counts ?? (tenants.data ? { ...tenants.data.status_counts, signups: tenants.data.signup_count } : undefined)

  const options = ORDER.map((v) => ({
    value: v,
    label: LABEL[v],
    count: v === 'deleted' ? deleted.data?.total : listCounts?.[v],
  }))

  const onChange = (v: TenantView) => {
    if (v === value) return
    if (onView && v !== 'deleted') onView(v)
    else navigate(viewPath(v))
  }

  return <FilterChips label="Filter by status" options={options} value={value} onChange={onChange} />
}
